import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileArrowDown, FileXls, MagnifyingGlass } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

// Hidden from the report picker: QA / test / retired master rows (not real customers).
const isTestCustomer = (c) => {
  const pc = String(c.ms_pcode || "").toUpperCase();
  const nm = String(c.name || "").toUpperCase();
  return pc.includes("QATEST") || pc.endsWith("_OLD") || nm.includes("QA TEST") || nm.includes("TEST CUSTOMER");
};

export default function CustomerStatusReport() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/customers");
        if (!alive) return;
        const rows = (data || [])
          .filter((c) => c.ms_pcode && !isTestCustomer(c))
          .sort((a, b) =>
            String(a.name || a.ms_pcode || "").localeCompare(String(b.name || b.ms_pcode || "")));
        setCustomers(rows);
      } catch (e) {
        toast.error("Could not load the customer list");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      String(c.name || "").toLowerCase().includes(q) ||
      String(c.ms_pcode || "").toLowerCase().includes(q));
  }, [customers, search]);

  const download = async (pcode) => {
    setBusy(pcode);
    try {
      const params = new URLSearchParams({ pcode, _: String(Date.now()) });
      if (dFrom) params.set("date_from", dFrom);
      if (dTo) params.set("date_to", dTo);
      const { data } = await api.get(
        `/ms/reports/customer-status.xlsx?${params.toString()}`,
        { responseType: "blob", headers: { "Cache-Control": "no-cache" } });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Order_Status_Report_${pcode}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${pcode} Customer Order Status report downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the status report");
    } finally {
      setBusy("");
    }
  };

  return (
    <Layout breadcrumb={["Home", "Dispatch", "Customer Order Status Report"]}>
      <div data-testid="customer-status-report-page" style={{ maxWidth: 1120, margin: "0 auto", padding: "8px 4px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <FileArrowDown size={22} weight="bold" color="#0EA5E9" />
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "#0F172A" }}>
            Customer Open Order Status Report
          </h1>
        </div>
        <p style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, margin: "0 0 18px" }}>
          Download the live open-order status workbook (Excel) for any tracked customer — ordered vs
          shipped vs open, by PO and item. Built on demand from the ERP; a fresh copy each time.
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ position: "relative", maxWidth: 380, flex: "1 1 260px" }}>
            <MagnifyingGlass size={16} weight="bold" color="#94A3B8"
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input
              data-testid="customer-status-report-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer or code…"
              style={{
                width: "100%", padding: "9px 12px 9px 34px", border: "1px solid #E2E8F0",
                borderRadius: 10, fontSize: 13, outline: "none", background: "#fff",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>Invoice date:</span>
            <input
              type="date"
              data-testid="customer-status-report-date-from"
              value={dFrom}
              onChange={(e) => setDFrom(e.target.value)}
              style={{ padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 10, fontSize: 13, background: "#fff" }}
            />
            <span style={{ color: "#94A3B8" }}>→</span>
            <input
              type="date"
              data-testid="customer-status-report-date-to"
              value={dTo}
              onChange={(e) => setDTo(e.target.value)}
              style={{ padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 10, fontSize: 13, background: "#fff" }}
            />
            {(dFrom || dTo) && (
              <button
                data-testid="customer-status-report-date-clear"
                className="jde-btn"
                onClick={() => { setDFrom(""); setDTo(""); }}
                style={{ fontSize: 12 }}
              >Clear</button>
            )}
          </div>
        </div>
        {(dFrom || dTo) && (
          <p style={{ fontSize: 12, color: "#0369A1", background: "#E0F2FE", border: "1px solid #BAE6FD", borderRadius: 8, padding: "6px 10px", margin: "0 0 16px" }} data-testid="customer-status-report-date-note">
            Shipped lines limited to invoices dated {dFrom || "…"} → {dTo || "…"}. Open (not-yet-shipped) orders are always included.
          </p>
        )}

        {loading ? (
          <div className="muted" style={{ padding: 24, textAlign: "center" }} data-testid="csr-loading">Loading customers…</div>
        ) : filtered.length === 0 ? (
          <div className="muted" style={{ padding: 24, textAlign: "center" }}>No customers match “{search}”.</div>
        ) : (
          <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }} data-testid="customer-status-report-grid">
            {filtered.map((c) => (
              <div
                key={c.ms_pcode}
                data-testid={`customer-status-report-card-${c.ms_pcode}`}
                style={{
                  display: "flex", flexDirection: "column", gap: 12, padding: "18px 18px 16px",
                  border: "1px solid #E2E8F0", borderRadius: 16, background: "#fff",
                  boxShadow: "0 1px 2px rgba(2,8,23,0.05)",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, flex: "0 0 auto", display: "grid",
                    placeItems: "center", background: "linear-gradient(180deg,#F0F9FF,#E0F2FE)",
                  }}>
                    <FileXls size={24} weight="bold" color="#0284C7" />
                  </div>
                  {c.total_open_orders > 0 && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: "#0369A1", background: "#E0F2FE",
                      padding: "2px 9px", borderRadius: 999, whiteSpace: "nowrap",
                    }}>{c.total_open_orders} open</span>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#0F172A" }}>{c.ms_pcode}</div>
                  <div style={{ fontSize: 12.5, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.name}>{c.name}</div>
                </div>
                <button
                  data-testid={`customer-status-report-download-${c.ms_pcode}`}
                  className="jde-btn jde-btn-primary"
                  onClick={() => download(c.ms_pcode)}
                  disabled={!!busy}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, whiteSpace: "nowrap", width: "100%", marginTop: "auto" }}
                >
                  <FileArrowDown size={15} weight="bold" />
                  {busy === c.ms_pcode ? "Building…" : "Download"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
